/**
 * CodexSpotlight — first-encounter tutorial popup.
 *
 * Polls the stage's finder each frame for any codex entry (food /
 * predator / concept) that is visible on-screen and not yet
 * tutorialized. When a match appears, pauses the game, draws a pulsing
 * ring around the entity, shows a DOM card with the codex text, and
 * emits 'codex:unlocked' (the codex system dedupes). Once dismissed,
 * marks tutorialized in meta so it never fires again in the same run.
 *
 * Architecture: each stage owns one of these. Stage provides the
 * `finder` callback that resolves a codex id to a screen-space target
 * (or null if no live instance is visible). Visibility-driven rather
 * than unlock-driven: predators in Phase 1 unlock on kill (after the
 * entity is gone) and parasites in Phase 2 unlock on contact, so we
 * trigger when the player first sees them, not when the codex unlocks.
 */

import { Application, Container, Graphics, Ticker } from 'pixi.js';
import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import type { CodexEntry } from '@/data/types';

export interface SpotlightTarget {
  /** Screen-space x in CSS pixels. */
  x: number;
  /** Screen-space y in CSS pixels. */
  y: number;
  /** Entity radius in screen px; ring draws ~1.8× this. */
  radius: number;
}

export type EntityFinder = (codexId: string) => SpotlightTarget | null;

const SPOTLIGHT_CATEGORIES = new Set(['food', 'predator', 'concept']);

export class CodexSpotlight {
  private app: Application;
  private fxLayer: Container;
  private defs: Record<string, CodexEntry>;
  private finder: EntityFinder;
  private icons?: Map<string, HTMLCanvasElement>;
  private iconUrlCache = new Map<string, string>();
  private pending: string[] = [];
  private ring: Graphics | null = null;
  private card: HTMLElement | null = null;
  private active = false;
  private ringT = 0;
  private ringX = 0;
  private ringY = 0;
  private ringR = 30;
  private offDismiss: (() => void) | null = null;
  private tickerFn: ((t: Ticker) => void) | null = null;

  constructor(
    app: Application,
    fxLayer: Container,
    defs: Record<string, CodexEntry>,
    finder: EntityFinder,
    icons?: Map<string, HTMLCanvasElement>,
  ) {
    this.app = app;
    this.fxLayer = fxLayer;
    this.defs = defs;
    this.finder = finder;
    this.icons = icons;
    this.rebuildPending();
    // Pulse + visibility poll runs off the Pixi ticker so it keeps
    // animating while the game-loop is paused (Game.tick short-circuits
    // on paused, but ticker callbacks still fire).
    this.tickerFn = (t: Ticker) => this.update(t.deltaMS / 1000);
    this.app.ticker.add(this.tickerFn);
  }

  /** True while a card is up (the game is paused under it). */
  isActive(): boolean { return this.active; }

  private rebuildPending(): void {
    const meta = saveManager.getMeta();
    if (!meta.codexTutorialized) meta.codexTutorialized = [];
    const seen = new Set(meta.codexTutorialized);
    this.pending = [];
    for (const [id, entry] of Object.entries(this.defs)) {
      if (!SPOTLIGHT_CATEGORIES.has(entry.category)) continue;
      if (seen.has(id)) continue;
      this.pending.push(id);
    }
  }

  private tryShowVisible(): void {
    if (this.active || this.pending.length === 0) return;
    for (let i = 0; i < this.pending.length; i++) {
      const id = this.pending[i];
      const entry = this.defs[id];
      if (!entry) {
        this.pending.splice(i, 1);
        i--;
        continue;
      }
      const target = this.finder(id);
      if (!target) continue;

      const meta = saveManager.getMeta();
      if (!meta.codexTutorialized) meta.codexTutorialized = [];
      meta.codexTutorialized.push(id);
      saveManager.save();
      this.pending.splice(i, 1);
      // Also unlock the codex if it hasn't already — first encounter
      // by sight should populate the codex panel. The codex system
      // dedupes so this is safe even if the entry is already unlocked.
      bus.emit('codex:unlocked', { entryId: id });
      this.show(entry, target);
      return;
    }
  }

  private show(entry: CodexEntry, target: SpotlightTarget): void {
    this.active = true;
    bus.emit('game:paused', undefined);
    document.body.classList.add('show-cursor');

    this.ringX = target.x;
    this.ringY = target.y;
    this.ringR = Math.max(28, target.radius * 1.8);
    this.ringT = 0;
    this.ring = new Graphics();
    this.fxLayer.addChild(this.ring);
    this.drawRing();

    this.card = this.buildCard(entry);
    // Place card on the opposite side from the target so it doesn't cover
    // what it's describing. Default is bottom; if the target sits in the
    // lower 55% of the viewport, flip the card to the top instead.
    const vh = window.innerHeight;
    if (target.y > vh * 0.45) {
      this.card.classList.add('csl-top');
    }
    document.body.appendChild(this.card);
    requestAnimationFrame(() => this.card?.classList.add('visible'));

    const onDown = (e: PointerEvent) => {
      const tgt = e.target as Node | null;
      if (this.card && tgt && this.card.contains(tgt)) {
        if ((tgt as HTMLElement).classList.contains('csl-continue')) {
          this.dismiss();
        }
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      this.dismiss();
    };
    window.addEventListener('pointerdown', onDown, { capture: true });
    this.offDismiss = () => window.removeEventListener('pointerdown', onDown, { capture: true } as EventListenerOptions);
  }

  private buildCard(entry: CodexEntry): HTMLElement {
    const card = document.createElement('div');
    card.className = 'codex-spotlight-card';

    // Icon — same canvas-rendered silhouette used in the codex panel.
    // Lets the player visually anchor the popup text to the entity on
    // screen even if their eyes are still drawn to the spotlight ring.
    const iconImg = this.iconImg(entry.id, 56);
    if (iconImg) {
      const wrap = document.createElement('div');
      wrap.className = 'csl-icon';
      wrap.appendChild(iconImg);
      card.appendChild(wrap);
    }

    const label = document.createElement('div');
    label.className = 'csl-label';
    label.textContent = 'First encounter';
    card.appendChild(label);

    const title = document.createElement('div');
    title.className = 'csl-title';
    title.textContent = entry.title;
    card.appendChild(title);

    if (entry.flavor) {
      const flavor = document.createElement('div');
      flavor.className = 'csl-flavor';
      flavor.textContent = entry.flavor;
      card.appendChild(flavor);
    }

    if (entry.science) {
      const science = document.createElement('div');
      science.className = 'csl-science';
      science.textContent = entry.science;
      card.appendChild(science);
    }

    const cont = document.createElement('button');
    cont.className = 'csl-continue';
    cont.textContent = 'Continue';
    card.appendChild(cont);

    return card;
  }

  /** Build an <img> for the codex entry's silhouette, or null if missing. */
  private iconImg(entryId: string, sizePx: number): HTMLImageElement | null {
    if (!this.icons) return null;
    const canvas = this.icons.get(entryId);
    if (!canvas) return null;
    let url = this.iconUrlCache.get(entryId);
    if (!url) {
      url = canvas.toDataURL('image/png');
      this.iconUrlCache.set(entryId, url);
    }
    const img = document.createElement('img');
    img.src = url;
    img.width = sizePx;
    img.height = sizePx;
    img.alt = '';
    return img;
  }

  private dismiss(): void {
    if (!this.active) return;
    this.active = false;
    bus.emit('game:resumed', undefined);
    document.body.classList.remove('show-cursor');
    if (this.ring) {
      this.ring.destroy();
      this.ring = null;
    }
    if (this.card) {
      this.card.remove();
      this.card = null;
    }
    this.offDismiss?.();
    this.offDismiss = null;
  }

  private drawRing(): void {
    if (!this.ring) return;
    const r = this.ringR;
    this.ring.clear();
    this.ring.circle(this.ringX, this.ringY, r * 1.6).fill({ color: 0xfffacc, alpha: 0.06 });
    this.ring.circle(this.ringX, this.ringY, r * 1.2).fill({ color: 0xfff39a, alpha: 0.10 });
    this.ring.circle(this.ringX, this.ringY, r).stroke({ color: 0xfff39a, width: 3, alpha: 0.95 });
    this.ring.circle(this.ringX, this.ringY, r * 0.85).stroke({ color: 0xffffff, width: 1.2, alpha: 0.6 });
  }

  private update(dt: number): void {
    if (this.active) {
      if (!this.ring) return;
      this.ringT += dt;
      const pulse = 1 + Math.sin(this.ringT * 4.0) * 0.08;
      this.ring.position.set(this.ringX * (1 - pulse), this.ringY * (1 - pulse));
      this.ring.scale.set(pulse);
      return;
    }
    this.tryShowVisible();
  }

  destroy(): void {
    if (this.tickerFn) {
      this.app.ticker.remove(this.tickerFn);
      this.tickerFn = null;
    }
    this.dismiss();
  }
}
